import moment from "moment";
import { orderStatus } from "../constants/orderStatus";
import { formatCuit, limpiarCuit } from "./cuit";

const SORT_COLUMN_MAP = {
  orderId: "orderId",
  status: "status",
  date: "createdAt",
  time: "createdAt",
  totalAmount: "subTotal",
  employeeName: "employeeId",
  "client.company": "clientId",
};

const ORDERS_SELECT = `
  orderId,
  createdAt,
  status,
  currencyRate,
  subTotal,
  iva105,
  iva210,
  bucketId,
  clientId,
  employeeId,
  client: clientId(company, name, lastName),
  employee: employeeId(*)
`;

export const getOrdersSelect = () => ORDERS_SELECT;

export const formatEmployeeName = (employee) => {
  if (!employee) return "";
  return `${employee.name || ""} ${employee.lastName || ""}`.trim();
};

export const mapOrderRow = (order) => {
  const date = moment(order.createdAt).format("DD/MM/YY");
  const dateObject = new Date(order.createdAt);
  const hours = String(dateObject.getHours()).padStart(2, "0");
  const minutes = String(dateObject.getMinutes()).padStart(2, "0");
  const seconds = String(dateObject.getSeconds()).padStart(2, "0");
  const subTotal = parseFloat(order.subTotal) || 0;
  const iva105 = parseFloat(order.iva105) || 0;
  const iva210 = parseFloat(order.iva210) || 0;

  return {
    ...order,
    date,
    time: `${hours}:${minutes}:${seconds}`,
    employeeName: formatEmployeeName(order.employee) || "Sin operador",
    totalAmount: subTotal + iva105 + iva210,
  };
};

const escapeSearchTerm = (term) =>
  term.replace(/[,()]/g, " ").trim();

/**
 * PostgREST no permite .or() sobre columnas embebidas (client.company).
 * Resolvemos clientId / employeeId en tablas propias y filtramos orders por FK.
 */
export const buildOrdersSearchOrFilter = async (supabase, searchText) => {
  const term = escapeSearchTerm(searchText);
  if (!term) return null;

  const orParts = [];
  const ilikePattern = `%${term}%`;

  if (/^\d+$/.test(term)) {
    orParts.push(`orderId.eq.${term}`);
  }

  const matchingStatuses = orderStatus
    .filter((s) => s.label.toLowerCase().includes(term.toLowerCase()))
    .map((s) => s.value);

  if (matchingStatuses.length > 0) {
    orParts.push(`status.in.(${matchingStatuses.join(",")})`);
  }

  // El CUIT se guarda con y sin guiones según cómo se haya cargado el cliente,
  // así que se busca por lo tecleado, por los dígitos solos y por el formateado.
  // Sólo se busca por CUIT con 8 dígitos o más: con menos, un número es casi
  // siempre un N° de orden y buscarlo como CUIT ensanchaba los resultados.
  const soloDigitos = limpiarCuit(term);
  const pareceCuit = soloDigitos.length >= 8;
  const cuitPatterns = pareceCuit
    ? [`%${soloDigitos}%`, ...(soloDigitos.length === 11 ? [`%${formatCuit(soloDigitos)}%`] : [])]
    : [];
  if (/[-.\s]/.test(term) && pareceCuit) cuitPatterns.push(ilikePattern);

  const [byCompany, byClientName, byClientLastName, ...byCuit] = await Promise.all([
    supabase.from("clients").select("clientId").ilike("company", ilikePattern),
    supabase.from("clients").select("clientId").ilike("name", ilikePattern),
    supabase.from("clients").select("clientId").ilike("lastName", ilikePattern),
    ...cuitPatterns.map((pattern) =>
      supabase.from("clients").select("clientId").ilike("cuit", pattern)
    ),
  ]);

  if (byCompany.error) throw new Error(byCompany.error.message);
  if (byClientName.error) throw new Error(byClientName.error.message);
  if (byClientLastName.error) throw new Error(byClientLastName.error.message);

  const clientIds = [
    ...new Set(
      [
        ...(byCompany.data || []),
        ...(byClientName.data || []),
        ...(byClientLastName.data || []),
        ...byCuit.flatMap((result) => (result.error ? [] : result.data || [])),
      ].map((c) => c.clientId)
    ),
  ];

  if (clientIds.length) {
    orParts.push(`clientId.in.(${clientIds.join(",")})`);
  }

  const [byName, byLastName] = await Promise.all([
    supabase.from("employees").select("employeeId").ilike("name", ilikePattern),
    supabase
      .from("employees")
      .select("employeeId")
      .ilike("lastName", ilikePattern),
  ]);

  if (byName.error) throw new Error(byName.error.message);
  if (byLastName.error) throw new Error(byLastName.error.message);

  const employeeIds = [
    ...new Set(
      [...(byName.data || []), ...(byLastName.data || [])].map(
        (e) => e.employeeId
      )
    ),
  ];

  if (employeeIds.length) {
    orParts.push(`employeeId.in.(${employeeIds.join(",")})`);
  }

  if (orParts.length === 0) {
    return "orderId.eq.-1";
  }

  return orParts.join(",");
};

export const applyOrdersSearch = (query, orFilter) => {
  if (!orFilter) return query;
  return query.or(orFilter);
};

/**
 * Filtros de la barra propia (chips de estado y operador). Reemplazan al
 * `filterList` de mui-datatables, que dependía del orden de las columnas.
 */
export const applyDirectFilters = (query, { status, employeeId } = {}) => {
  let next = query;
  if (status !== null && status !== undefined) next = next.eq("status", status);
  if (employeeId !== null && employeeId !== undefined) next = next.eq("employeeId", employeeId);
  return next;
};

export const applyOrdersFilters = (query, filterList, columns, employees) => {
  if (!filterList?.length || !columns?.length) return query;

  columns.forEach((col, index) => {
    const filterValue = filterList[index];
    if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) {
      return;
    }

    const value = Array.isArray(filterValue) ? filterValue[0] : filterValue;
    if (!value) return;

    if (col.name === "employeeName") {
      const employee = employees.find(
        (emp) => formatEmployeeName(emp) === value
      );
      if (employee?.employeeId) {
        query = query.eq("employeeId", employee.employeeId);
      }
    }

    if (col.name === "status") {
      const status = orderStatus.find((s) => s.label === value);
      if (status) {
        query = query.eq("status", status.value);
      }
    }
  });

  return query;
};

export const getSortColumn = (sortOrder) => {
  const name = sortOrder?.name || "orderId";
  return SORT_COLUMN_MAP[name] || "orderId";
};

export const getSortAscending = (sortOrder) => sortOrder?.direction === "asc";
